import React, { useState, useEffect, useRef, useCallback } from 'react';
import './accountpage.css';
import { useLocation } from 'react-router-dom';
import { useUser } from './context/UserContext';
import { useDropzone } from 'react-dropzone';
import PaymentMethodPage from './components/PaymentMethodPage';
import { useOptionalTenant } from './tenant/TenantContext';
import {
  fetchMyProfile,
  saveMyProfile,
  requestProfileAvatarUploadUrl,
  requestCompanyLogoUploadUrl,
  fetchCompanyAccount,
  saveCompanyAccount,
  mapAccountToCompanyForm,
  mapProfileToForm,
} from './services/accountService';
import {
  uploadImageViaPresignedUrl,
  uploadFailureMessage,
} from './utils/imageUpload';
import ProfileAvatarCircle from './components/ProfileAvatarCircle';
import {
  AVATAR_SOURCE_AUTH0_FALLBACK,
  PROFILE_CHROME_SOURCE_KK_PROFILE,
} from './auth/profileIdentity';

function formatEffectiveRoleLabel(role) {
  const raw = String(role || '').trim();
  if (!raw) return null;
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

const emptyProfileForm = () => ({
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  avatar: null,
  avatarKey: null,
});

function pickCompanyLogoFields(account) {
  const { logo, logoKey } = mapAccountToCompanyForm(account);
  return { logo, logoKey };
}

const emptyCompanyForm = () => ({
  name: '',
  phoneNumber: '',
  accountType: '',
  streetAddress: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
  // COMPANY logo (MasterAccount.companyLogo) — presigned display URL + key.
  logo: null,
  logoKey: null,
});

const AccountPage = () => {
  const location = useLocation();
  const { userInfo, updateUserInfo } = useUser();
  const tenantCtx = useOptionalTenant();
  const effectiveRole = tenantCtx?.tenant?.effectiveRole || null;
  const roleLabel =
    formatEffectiveRoleLabel(effectiveRole) ||
    formatEffectiveRoleLabel(userInfo?.role) ||
    'Account';
  const canEditCompany = effectiveRole === 'owner' || effectiveRole === 'admin';

  const [isEditing, setIsEditing] = useState(false);
  const [isProfileEditing, setIsProfileEditing] = useState(false);
  const [profileForm, setProfileForm] = useState(emptyProfileForm);
  const [companyData, setCompanyData] = useState(emptyCompanyForm);
  const [companyPermissions, setCompanyPermissions] = useState({
    canEditCompany: false,
  });
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  const [activeMenu, setActiveMenu] = useState(null);
  const [editingDevice, setEditingDevice] = useState(null);
  const [devices, setDevices] = useState({ 0: '(481) 491-2048', 1: '' });
  const actionMenuRef = useRef(null);
  const [defaultMethod, setDefaultMethod] = useState(0);

  const syncChromeFromProfile = useCallback(
    (form) => {
      if (!form || !updateUserInfo) return;
      // Session chrome only — not Account SoR. Do not merge prior chrome blob.
      // profileSource marks this as canonical kk-profile chrome so the shell
      // header may render the same avatar/name (incl. Support Mode, where
      // UserContext binds it to the selected user's sub).
      updateUserInfo({
        firstName: form.firstName || '',
        lastName: form.lastName || '',
        email: form.email || '',
        avatar: form.avatar || null,
        phoneNumber: form.phoneNumber || '',
        profileSource: PROFILE_CHROME_SOURCE_KK_PROFILE,
      });
    },
    [updateUserInfo],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [profilePayload, accountPayload] = await Promise.all([
          fetchMyProfile(),
          fetchCompanyAccount(),
        ]);
        if (cancelled) return;
        const form = mapProfileToForm(profilePayload?.profile);
        setProfileForm(form);
        syncChromeFromProfile(form);
        setCompanyData(mapAccountToCompanyForm(accountPayload?.account));
        setCompanyPermissions(accountPayload?.permissions || {});
      } catch (err) {
        if (!cancelled) {
          setLoadError(err?.message || 'Failed to load Account');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantCtx?.tenant?.masterAccountId, syncChromeFromProfile]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setActiveMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCompanyEditToggle = async () => {
    if (!canEditCompany && !isEditing) return;
    if (isEditing) {
      setSaving(true);
      setSaveError(null);
      try {
        const saved = await saveCompanyAccount({
          billingPhone: companyData.phoneNumber,
          address_street: companyData.streetAddress,
          address_city: companyData.city,
          address_state: companyData.state,
          address_zipCode: companyData.zipCode,
          address_country: companyData.country,
        });
        setCompanyData(mapAccountToCompanyForm(saved?.account));
        setCompanyPermissions(saved?.permissions || companyPermissions);
        setIsEditing(false);
      } catch (err) {
        setSaveError(err?.message || 'Failed to save company profile');
      } finally {
        setSaving(false);
      }
      return;
    }
    setIsEditing(true);
  };

  const handleChange = (field, value) => {
    setCompanyData((prev) => ({ ...prev, [field]: value }));
  };

  const handleProfileEditToggle = async () => {
    if (isProfileEditing) {
      setSaving(true);
      setSaveError(null);
      try {
        const saved = await saveMyProfile({
          firstName: profileForm.firstName,
          lastName: profileForm.lastName,
          phone: profileForm.phoneNumber,
          ...(profileForm.avatarKey ? { avatar: profileForm.avatarKey } : {}),
        });
        const form = mapProfileToForm(saved?.profile);
        setProfileForm(form);
        syncChromeFromProfile(form);
        setIsProfileEditing(false);
      } catch (err) {
        setSaveError(err?.message || 'Failed to save profile');
      } finally {
        setSaving(false);
      }
      return;
    }
    setIsProfileEditing(true);
  };

  const handlePersonalInfoChange = (field, value) => {
    setProfileForm((prev) => ({ ...prev, [field]: value }));
  };

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (!acceptedFiles.length || avatarUploading) return;
      const file = acceptedFiles[0];
      const previousForm = profileForm;
      setSaveError(null);
      setAvatarUploading(true);
      try {
        // PERSON avatar → kk-profile (self-scoped key minted by the server).
        const key = await uploadImageViaPresignedUrl({
          requestUploadUrl: requestProfileAvatarUploadUrl,
          file,
          subject: 'avatar',
        });
        const saved = await saveMyProfile({ avatar: key });
        const form = mapProfileToForm(saved?.profile);
        setProfileForm(form);
        syncChromeFromProfile(form);
      } catch (err) {
        setProfileForm(previousForm);
        syncChromeFromProfile(previousForm);
        setSaveError(
          uploadFailureMessage(err, { subject: 'avatar', fallback: 'Failed to upload avatar' }),
        );
      } finally {
        setAvatarUploading(false);
      }
    },
    [avatarUploading, profileForm, syncChromeFromProfile],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: false,
    disabled: !isProfileEditing || avatarUploading,
    noKeyboard: false,
  });

  // COMPANY logo → MasterAccount.companyLogo. Company-scoped key minted by
  // the server from the authorized active MA; persisted via PATCH /me/account.
  // Never touches kk-profile / personal chrome (no syncChromeFromProfile).
  const canEditCompanyLogo = companyPermissions.companyLogoEditable === true;

  const onCompanyLogoDrop = useCallback(
    async (acceptedFiles) => {
      if (!acceptedFiles.length || logoUploading || !canEditCompanyLogo) return;
      const file = acceptedFiles[0];
      setSaveError(null);
      setLogoUploading(true);
      try {
        const key = await uploadImageViaPresignedUrl({
          requestUploadUrl: requestCompanyLogoUploadUrl,
          file,
          subject: 'company logo',
        });
        const saved = await saveCompanyAccount({ companyLogo: key });
        // Only the logo fields — never clobber unsaved phone/address edits.
        setCompanyData((prev) => ({ ...prev, ...pickCompanyLogoFields(saved?.account) }));
        setCompanyPermissions(saved?.permissions || companyPermissions);
      } catch (err) {
        setSaveError(
          uploadFailureMessage(err, {
            subject: 'company logo',
            fallback: 'Failed to upload company logo',
          }),
        );
      } finally {
        setLogoUploading(false);
      }
    },
    [logoUploading, canEditCompanyLogo, companyPermissions],
  );

  const {
    getRootProps: getLogoRootProps,
    getInputProps: getLogoInputProps,
    isDragActive: isLogoDragActive,
  } = useDropzone({
    onDrop: onCompanyLogoDrop,
    accept: { 'image/*': [] },
    multiple: false,
    disabled: !isEditing || !canEditCompanyLogo || logoUploading,
    noKeyboard: false,
  });

  const handleRemoveCompanyLogo = async () => {
    if (logoUploading || !canEditCompanyLogo) return;
    setSaveError(null);
    setLogoUploading(true);
    try {
      const saved = await saveCompanyAccount({ companyLogo: null });
      setCompanyData((prev) => ({ ...prev, ...pickCompanyLogoFields(saved?.account) }));
      setCompanyPermissions(saved?.permissions || companyPermissions);
    } catch (err) {
      setSaveError(err?.message || 'Failed to remove company logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleRemoveImage = async () => {
    if (avatarUploading) return;
    const previousForm = profileForm;
    setSaveError(null);
    setAvatarUploading(true);
    try {
      const saved = await saveMyProfile({ avatar: null });
      const form = mapProfileToForm(saved?.profile);
      setProfileForm(form);
      syncChromeFromProfile(form);
    } catch (err) {
      setProfileForm(previousForm);
      syncChromeFromProfile(previousForm);
      setSaveError(err?.message || 'Failed to remove avatar');
    } finally {
      setAvatarUploading(false);
    }
  };

  const toggleActionMenu = (index) => {
    setActiveMenu(activeMenu === index ? null : index);
  };
  const handleUpdateClick = (index) => {
    setEditingDevice(index);
    setActiveMenu(null);
  };
  const handleDeviceChange = (index, value) => {
    setDevices((prev) => ({ ...prev, [index]: value }));
  };
  const handleDeviceBlur = () => setEditingDevice(null);

  const showCompanyEdit = canEditCompany || companyPermissions.canEditCompany;

  // Same precedence as the header: canonical kk-profile avatar, else the
  // Auth0 display fallback UserContext resolved for this direct-login user.
  const auth0FallbackAvatar =
    userInfo?.avatarSource === AVATAR_SOURCE_AUTH0_FALLBACK && !userInfo?.supportMode
      ? userInfo.avatar
      : null;
  const profileDisplayUrl = profileForm.avatar || auth0FallbackAvatar;

  const renderMyProfile = () => (
    <>
      <div className="profile-header">
        <h2>My Profile</h2>
        <button
          className="edit-btn"
          onClick={handleProfileEditToggle}
          disabled={saving || loading}
        >
          <span className="edit-icon">{isProfileEditing ? '💾' : '✏️'}</span>
          {isProfileEditing ? (saving ? 'Saving…' : 'Save') : 'Edit'}
        </button>
      </div>
      <div className="profile-info">
        <div className="user-profile">
          <ProfileAvatarCircle
            editMode={isProfileEditing}
            displayUrl={profileDisplayUrl}
            firstName={profileForm.firstName}
            lastName={profileForm.lastName}
            email={profileForm.email}
            uploading={avatarUploading}
            identityLoading={loading}
            dragActive={isProfileEditing && isDragActive}
            rootProps={getRootProps()}
            inputProps={getInputProps()}
            onRemove={profileForm.avatar ? handleRemoveImage : null}
          />
          <div className="user-details">
            <h3>
              {`${profileForm.firstName || ''} ${profileForm.lastName || ''}`.trim() ||
                'Account'}
            </h3>
            <p className="role">{roleLabel}</p>
            <p className="email">{profileForm.email || '—'}</p>
          </div>
        </div>
        <div className="divider"></div>
        <div className="personal-info">
          <h3>Personal Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>First Name</label>
              {isProfileEditing ? (
                <input
                  type="text"
                  value={profileForm.firstName || ''}
                  onChange={(e) =>
                    handlePersonalInfoChange('firstName', e.target.value)
                  }
                  className="edit-input"
                />
              ) : (
                <p>{profileForm.firstName}</p>
              )}
            </div>
            <div className="info-item">
              <label>Last Name</label>
              {isProfileEditing ? (
                <input
                  type="text"
                  value={profileForm.lastName || ''}
                  onChange={(e) =>
                    handlePersonalInfoChange('lastName', e.target.value)
                  }
                  className="edit-input"
                />
              ) : (
                <p>{profileForm.lastName}</p>
              )}
            </div>
            <div className="info-item">
              <label>Phone Number</label>
              {isProfileEditing ? (
                <input
                  type="text"
                  value={profileForm.phoneNumber || ''}
                  onChange={(e) =>
                    handlePersonalInfoChange('phoneNumber', e.target.value)
                  }
                  className="edit-input"
                />
              ) : (
                <p>{profileForm.phoneNumber}</p>
              )}
            </div>
            <div className="info-item">
              <label>
                Email Address <span className="lock-icon">🔒</span>
              </label>
              <p>{profileForm.email || '—'}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderSecurity = () => (
    <>
      <div className="profile-header">
        <h2>Security</h2>
      </div>
      <div className="security-content">
        <div className="two-factor-section">
          <div className="section-header">
            <h3>Two-step authentication</h3>
            <button className="add-method-btn" type="button">
              <span>+</span> Add Method
            </button>
          </div>
          <div className="auth-methods-table">
            <div className="table-header">
              <div>Method</div>
              <div>Device</div>
              <div>Date Added</div>
              <div>Actions</div>
            </div>
            <div className="table-row">
              <div className="method-cell">
                <span>SMS</span>
                {defaultMethod === 0 && (
                  <span className="default-badge">Default</span>
                )}
              </div>
              <div>
                {editingDevice === 0 ? (
                  <input
                    type="text"
                    value={devices[0]}
                    onChange={(e) => handleDeviceChange(0, e.target.value)}
                    onBlur={handleDeviceBlur}
                    className="edit-input"
                    autoFocus
                  />
                ) : (
                  devices[0]
                )}
              </div>
              <div>January 27, 2025</div>
              <div
                className="actions"
                onClick={() => toggleActionMenu(0)}
                ref={actionMenuRef}
              >
                ⋮
                {activeMenu === 0 && (
                  <div className="actions-dropdown">
                    <div
                      className="action-item"
                      onClick={() => handleUpdateClick(0)}
                    >
                      Update
                    </div>
                    <div className="action-item">Remove</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderCompanyProfile = () => (
    <>
      <div className="profile-header">
        <h2>Company Profile</h2>
        {showCompanyEdit ? (
          <button
            className="edit-btn"
            onClick={handleCompanyEditToggle}
            disabled={saving || loading}
          >
            <span className="edit-icon">{isEditing ? '💾' : '✏️'}</span>
            {isEditing ? (saving ? 'Saving…' : 'Save') : 'Edit'}
          </button>
        ) : null}
      </div>
      <div className="company-content">
        <div className="company-profile">
          <div className="user-profile">
            <ProfileAvatarCircle
              editMode={isEditing && canEditCompanyLogo}
              displayUrl={companyData.logo}
              firstName={companyData.name}
              uploading={logoUploading}
              identityLoading={loading}
              dragActive={isEditing && canEditCompanyLogo && isLogoDragActive}
              rootProps={getLogoRootProps()}
              inputProps={getLogoInputProps()}
              onRemove={handleRemoveCompanyLogo}
              testIdPrefix="ma-company-logo"
              imageAlt="Company logo"
              uploadLabel="Upload Logo"
              removeLabel="Remove logo"
            />
            <div className="user-details">
              <h3>{companyData.name || '—'}</h3>
              <p className="role">{companyData.accountType || '—'}</p>
              <p className="email">{companyData.phoneNumber || '—'}</p>
            </div>
          </div>
          <div className="divider"></div>
          <div className="company-info">
            <h3>Company Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>
                  Company Name <span className="lock-icon">🔒</span>
                </label>
                <p>{companyData.name || '—'}</p>
              </div>
              <div className="info-item">
                <label>Company Phone Number</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={companyData.phoneNumber}
                    onChange={(e) => handleChange('phoneNumber', e.target.value)}
                    className="edit-input"
                  />
                ) : (
                  <p>{companyData.phoneNumber || '—'}</p>
                )}
              </div>
              <div className="info-item">
                <label>
                  Account type <span className="lock-icon">🔒</span>
                </label>
                <p>{companyData.accountType || '—'}</p>
              </div>
              <div className="info-item full-width">
                <label>Company Street Address</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={companyData.streetAddress}
                    onChange={(e) =>
                      handleChange('streetAddress', e.target.value)
                    }
                    className="edit-input"
                  />
                ) : (
                  <p>{companyData.streetAddress || '—'}</p>
                )}
              </div>
              <div className="info-item">
                <label>City</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={companyData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    className="edit-input"
                  />
                ) : (
                  <p>{companyData.city || '—'}</p>
                )}
              </div>
              <div className="info-item">
                <label>State</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={companyData.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    className="edit-input"
                  />
                ) : (
                  <p>{companyData.state || '—'}</p>
                )}
              </div>
              <div className="info-item">
                <label>ZIP / Postal code</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={companyData.zipCode}
                    onChange={(e) => handleChange('zipCode', e.target.value)}
                    className="edit-input"
                  />
                ) : (
                  <p>{companyData.zipCode || '—'}</p>
                )}
              </div>
              <div className="info-item">
                <label>Country</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={companyData.country}
                    onChange={(e) => handleChange('country', e.target.value)}
                    className="edit-input"
                  />
                ) : (
                  <p>{companyData.country || '—'}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderContent = () => {
    const path = location.pathname;
    if (path.includes('security')) return renderSecurity();
    if (path.includes('company')) return renderCompanyProfile();
    if (path.includes('payment')) return <PaymentMethodPage />;
    return renderMyProfile();
  };

  return (
    <div className="account-page">
      <h1>Account</h1>
      {loadError ? (
        <p className="account-error" role="alert">
          {loadError}
        </p>
      ) : null}
      {saveError ? (
        <p className="account-error" role="alert">
          {saveError}
        </p>
      ) : null}
      <div className="account-content">
        <div className="account-content-area full-width">{renderContent()}</div>
      </div>
    </div>
  );
};

export default AccountPage;
